import dayjs from 'dayjs';
import { PERMISSION_IDS } from '../constants/permissions';

export type VacancyPublishAction = 'publish' | 'unpublish' | 'none';

export interface VacancyPublishState {
  iconColor: string;
  iconTitle: string;
  isDisabled: boolean;
  action: VacancyPublishAction;
}

/**
 * Pure decision logic for vacancy job post publish/unpublish icon in Vacancy Search grid.
 */
export function getVacancyPublishState(
  row: {
    status: number;
    visibleStatus: number;
    approvalStatus: number;
    jobPostPublished: number;
    vacancy_EndDate: string;
  },
  permissionIds: number[],
): VacancyPublishState {
  const has = (id: number) => permissionIds.includes(id);
  const isEndDatePassed = row.vacancy_EndDate
    ? dayjs(row.vacancy_EndDate).isBefore(dayjs(), 'day')
    : false;
  const isPublishDisabled =
    row.status === 0 ||
    isEndDatePassed ||
    row.approvalStatus === 0 ||
    row.approvalStatus === 2 ||
    !has(PERMISSION_IDS.VACANCY_SEARCH_PUBLISH);
  const isUnpublishDisabled =
    row.approvalStatus === 0 ||
    row.approvalStatus === 2 ||
    row.jobPostPublished === 0 ||
    !has(PERMISSION_IDS.VACANCY_SEARCH_UNPUBLISH);

  if (Number(row.jobPostPublished) === 1) {
    return {
      iconColor: '#f70000',
      iconTitle: isUnpublishDisabled ? 'Unable to publish' : 'Unpublish Vacancy',
      isDisabled: isUnpublishDisabled,
      action: isUnpublishDisabled ? 'none' : 'unpublish',
    };
  }
  if (Number(row.status) === 1 && Number(row.visibleStatus) === 1) {
    return {
      iconColor: '#03b41bff',
      iconTitle: isPublishDisabled ? 'Unable to publish' : 'Publish Vacancy',
      isDisabled: isPublishDisabled,
      action: isPublishDisabled ? 'none' : 'publish',
    };
  }
  return {
    iconColor: '#9C9C9C',
    iconTitle: 'Unable to publish',
    isDisabled: true,
    action: 'none',
  };
}

export type VacancyEditAction = 'edit' | 'withdraw' | 'none';

export interface VacancyEditState {
  isDisabled: boolean;
  action: VacancyEditAction;
}

/**
 * Pure decision logic for Vacancy Search "Edit" link (draft vs approved flows).
 */
export function getVacancyEditState(
  approvalStatus: number,
  permissionIds: number[],
): VacancyEditState {
  const has = (id: number) => permissionIds.includes(id);

  if (approvalStatus >= 1) {
    if (has(PERMISSION_IDS.VACANCY_EDIT_APPROVED)) {
      return { isDisabled: false, action: 'edit' };
    }
    if (has(PERMISSION_IDS.VACANCY_SEARCH_WITHDRAW)) {
      return approvalStatus === 3
        ? { isDisabled: false, action: 'edit' }
        : { isDisabled: false, action: 'withdraw' };
    }
    return { isDisabled: true, action: 'none' };
  }
  return {
    isDisabled: !has(PERMISSION_IDS.VACANCY_EDIT_DRAFT),
    action: 'edit',
  };
}
